import React, { SyntheticEvent, useEffect, useState } from 'react'
import {convertSearchSpringProductPricingToMarketPrice, getInstantSearchSuggestions, getTrendingSuggestions, isSearchSpringProductResponse, standardSearch } from '../../helpers/searchSpringHelpers'
import SearchBarSuggestions from './SearchBarSuggestions'
import { CollectionResult, Result, SearchSpringProductResponse, ServiceData, TrendingSuggestions } from '../types/types'
import SearchInlay from './SearchInlay'
import SearchBar from './SearchBar'
import debounce from 'lodash.debounce'
import InstantSearchGrid from './InstantSearchGrid'
import ProductInstantSearchSuggestions from './ProductInstantSearchSuggestions'
import InstantCollectionOrPageResults from './InstantCollectionResults'
import useWindowSize from '../customHooks/useWindowSize'
import SearchHeader from './SearchHeader'

declare var Shopify: { currency: { active: string } }

const SearchInit = () => {
  const [searchTerms, setSearchTerms] = useState('')
  const [products, setProducts] = useState<Result[] | null>(null)
  const [collections, setCollections] = useState<CollectionResult[] | null>(null)
  const [pages, setPages] = useState<Result[] | null>(null)
  const [noResults, setNoResults] = useState(false)
  const [trendingSuggestions, setTrendingSuggestions] = useState<TrendingSuggestions | null>(null)
  const servicesData: ServiceData[] = CD_STATE?.searchDropdownData || []
  const [resultToDisplay, setResultToDisplay] = useState('')
  const [redirectResult, setRedirectResult] = useState<string>('')
  const windowSize = useWindowSize()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)


  const fetchAllResults = async (query: string) => {
    setLoading(true)
    const resultsPerPage = windowSize.width <= 640 ? CD_STATE.searchspring.numberOfProductsMobile : CD_STATE.searchspring.numberOfProductsDesktop
    const results: PromiseSettledResult<SearchSpringProductResponse | unknown>[] = await Promise.allSettled([
      getInstantSearchSuggestions(query, resultsPerPage ),
      standardSearch({searchQuery: query, siteIdType: 'collections' }),
      standardSearch({searchQuery: query, siteIdType: 'pages' })
    ])
    if (results[0].status === 'fulfilled' && isSearchSpringProductResponse(results[0].value)) {
      if (results[0].value.results.length) {
        // Converting all product data into correct pricing 
        if (Shopify.currency.active != "AUD") {
          const newproductData: SearchSpringProductResponse = await convertSearchSpringProductPricingToMarketPrice(results[0].value)
          // In the event the storefront API can't get data just use the AU pricing instead. 
          // They will see the correct price in the cart and product pages. 
          setProducts(newproductData?.results || results[0].value.results)
        } else {
          setProducts(results[0].value.results)
        }

        const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
        const redirectUrl = results[0].value.merchandising.redirect
        const redirectUrlWithLocale = !redirectUrl || !rootUrlWithoutSlash || (rootUrlWithoutSlash && redirectUrl.includes(rootUrlWithoutSlash))
          ? redirectUrl
          : rootUrlWithoutSlash + redirectUrl

        setResultToDisplay('products')
        setRedirectResult(redirectUrlWithLocale)
        setNoResults(false)
      } else {
        setProducts(null)
        setNoResults(true)
      }
    } else {
      setError(true)
    }

    if (results[1].status === 'fulfilled' && isSearchSpringProductResponse(results[1].value)) {
      setCollections(results[1].value.results)
    }

    if (results[2].status === 'fulfilled' && isSearchSpringProductResponse(results[2].value)) {
      setPages(results[2].value.results)
    }

    if (!query) {
      setTimeout(() => {
        setProducts((products) => products = null)
        setNoResults(false)
      }, 300)
    }

    setLoading(false)
  }

  const getPopularSuggestions = async () => {
    const trendingSuggestions = await getTrendingSuggestions()
    if (trendingSuggestions) {
      setTrendingSuggestions(trendingSuggestions)
    } else {
      setTrendingSuggestions(null)
    }
  }


  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    if (redirectResult) {
      window.location.href = redirectResult
      return
    }
    // Doing this here instead of in dispatch originalQuery to avoid DOM showing encoded query
    const encodedQuery = encodeURIComponent(searchTerms)
    const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
    location.replace(`${rootUrlWithoutSlash}/search?q=${encodedQuery}`)
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newSearchTerms = e.target.value
    setSearchTerms(newSearchTerms)      
    debounceFetchResults(newSearchTerms)
  }
  
  const debounceFetchResults = debounce((searchTerms: string) => {
    fetchAllResults(searchTerms)
  }, 400)


  function handleResultClick(e: React.MouseEvent<HTMLAnchorElement>) {
    const target = e.target as HTMLAnchorElement;
    setResultToDisplay(target.id)
  }

  useEffect(() => {
    getPopularSuggestions()
  },[])

  const ComponentHandler = () => {
    if (products) {
      switch (resultToDisplay) {
        case "products":
          return <>
            <InstantSearchGrid products={products} customClasses='mt-SM md:mt-[90px]'/>
            <div className="flex justify-center my-DLG">
              <button onClick={(e) => {handleSubmit(e)}}  className="Button Button--secondary-dark uppercase backdrop-filter-none min-w-[150px] font-calibre text-sm-b text-black hover:text-white">View All</button>
            </div>
          </>
        case "collections":
          return <InstantCollectionOrPageResults collectionResults={collections} searchTerm={searchTerms} />
        case "pages":
          return <InstantCollectionOrPageResults pageResults={pages} searchTerm={searchTerms} />
        default:
          return <>
            <InstantSearchGrid products={products} customClasses='mt-SM md:mt-[90px]'/>
            <div className="flex justify-center my-DLG">
              <button onClick={(e) => {handleSubmit(e)}}  className="Button Button--secondary-dark uppercase backdrop-filter-none min-w-[150px] font-calibre text-sm-b text-black hover:text-white">View All</button>
            </div>
          </>
      }
    }

    if (error) {
      return <div className="text-lg font-bstSpyre mt-4 text-center mx-5">Sorry, search is not working right now. Please try again later or contact us if this issue persists.</div>
    }
  }

  const ResultHandler = () => {
    if (products?.length && !error && !loading) {
      return <ProductInstantSearchSuggestions products={products} collections={collections} pages={pages} resultDisplaying={resultToDisplay} handleResultClick={handleResultClick}/>
    } else if (noResults && !loading && searchTerms) {
      return (
        <>
          <SearchBarSuggestions heading={'SUGGESTED TERMS:'} trendingSuggestions={trendingSuggestions}/>
          <div className='text-lg-heading px-5 md:text-xxl font-bstSpyre mt-[73px] md:mt-16 text-center'>
          {`Sorry, there are no results for "${searchTerms}"`}
          </div>
        </>
      )
    }
    
    return <SearchBarSuggestions heading={'SUGGESTED TERMS:'} trendingSuggestions={trendingSuggestions}/>
  }

  return (
    <>
      <SearchHeader setSearchTerms={setSearchTerms} setProducts={setProducts} setNoResults={setNoResults}/>
      <div className={`flex flex-col search-container items-center md:py-0 w-full max-w-screen-xl`}>
        <SearchBar handleSubmit={handleSubmit} handleChange={handleInput} searchValue={searchTerms} setSearchTerms={setSearchTerms} setProducts={setProducts} setNoResults={setNoResults} />
      </div>
      <ResultHandler />
      <ComponentHandler />
      {!noResults && !products ? <SearchInlay servicesData={servicesData}/> : null}
    </>
  )
}

export default SearchInit